import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    FormControl,
    ReactiveFormsModule,
    AbstractControl,
    NG_VALIDATORS,
    Validator,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dropdown-input-v3',
  providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownInputV3Component),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => DropdownInputV3Component),
            multi: true,
        },
    ],
    imports: [ReactiveFormsModule],

  templateUrl: './dropdown-input-v3.component.html',
  styleUrl: './dropdown-input-v3.component.scss',
})
export class DropdownInputV3Component implements ControlValueAccessor, Validator, OnInit {
  private translateService = inject(TranslateService);

    @Input() label: string = '';
    @Input() description: string = '';
    @Input() icon: string = '';
    @Input() placeholder: string = '';
    @Input() errorMessage: string = '';
    @Input() submitAttempted: boolean = false;
    @Input() hideError: boolean = false;
    @Input() formControlName: string = '';
    @Input() filled: boolean = false;
    @Input() options: (string | Record<string, any>)[] = [];
    @Input() displayProperty: string = '';
    @Input() valueProperty: string = '';
    @Input() extraClass: string = '';
    @Input() categoryProperty: string = ''; // New input for category grouping
    @Input() allowNull: boolean = false; // Allow null selection
    /** When false, hides the required asterisk and optional hint on the label (validation unchanged). */
    @Input() showRequiredOrOptional: boolean = true;

    control!: FormControl;

    required: boolean = false;
    value: any = '';
    isDisabled: boolean = false;
    hasErrors: boolean = false;

    onChange: (value: any) => void = () => {};
    onTouched: () => void = () => {};

    ngOnInit(): void {
        if (!this.errorMessage) {
            this.errorMessage = this.translateService.instant('GENERAL.invalidValue');
        }
        if (!this.control) {
            this.control = new FormControl({ value: '', disabled: this.isDisabled });
        }
    }

    writeValue(value: any): void {
        this.value = value || '';
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        if (this.control) {
            if (isDisabled && this.control.enabled) {
                this.control.disable();
            } else if (!isDisabled && this.control.disabled) {
                this.control.enable();
            }
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this.control = control as FormControl;
        this.required = control?.hasValidator(Validators.required);
        return null;
    }

    get isInvalid(): boolean {
        return this.control?.invalid && this.submitAttempted;
    }

    /**
     * Get the display text of an option based on displayProperty.
     */
    getOptionText(option: any): string {
        if (typeof option === 'object' && this.displayProperty) {
            return option[this.displayProperty] ?? '';
        }
        return option as string;
    }

    /**
     * Get the form value of an option based on valueProperty.
     */
    getOptionValue(option: any): any {
        if (typeof option === 'object' && this.valueProperty) {
            return option[this.valueProperty];
        }
        return option;
    }

    /**
     * Handles selection and updates the form control value.
     */
    handleSelection(option: any): void {
        this.value = this.getOptionValue(option);
        this.onChange(this.value);
        this.onTouched();
    }

    /**
     * Determines if options are grouped (i.e., have categories).
     */
    get hasCategories(): boolean {
        return !!this.categoryProperty && Array.isArray(this.options) && this.options.length > 0 && typeof this.options[0] === 'object' && this.categoryProperty in this.options[0];
    }

    /**
     * Returns the options for a given category group.
     */
    getCategoryOptions(categoryGroup: any): (string | Record<string, any>)[] {
        return categoryGroup.options || [];
    }

    /**
     * Returns the category label for a given category group.
     */
    getCategoryLabel(categoryGroup: any): string {
        return categoryGroup[this.categoryProperty] || '';
    }
}
